/**
 * Fixture rig — read from OPBridge, which owns it.
 *
 * WHY OPBRIDGE IS CANONICAL. A fixture instance says "there is a SlimPAR at
 * universe 1, address 1, in 3-channel mode". That is a fact about the RIG,
 * not about this iPad — the three-way data split puts rig truth on OPBridge
 * (DEC-076 does the same for projects), and OPBridge is the thing actually
 * driving the DMX interface. A locally-invented fixture list is a second
 * opinion about hardware only one of the two can see.
 *
 * CADENCE: fetched ON BRIDGE-CONNECT plus explicit refresh. NOT polled, and
 * not "once per session" either.
 *   - Not polled, because a rig changes when the operator repatches it, not
 *     continuously. Polling a static list is steady LAN + battery cost on a
 *     gig iPad, for nothing. Same reasoning as the Stream Deck catalogue.
 *   - Not once-per-session, because a session outlives a reconnect. A bridge
 *     that drops and returns is exactly when the list may have changed, and
 *     a once-only fetch would serve a stale rig for the rest of the night.
 * On-connect is strictly better than both and is this codebase's established
 * pattern (`installStreamDeckPrewarm`).
 *
 * STATUS IS MULTI-VALUED ON PURPOSE. "No fixtures configured", "we are not
 * allowed to ask" and "there is no bridge" are three different problems with
 * three different fixes, and collapsing them into an empty list is how the
 * retired Stream Deck stub misled everyone for a year. An empty rig must
 * never be indistinguishable from an unreachable one.
 *
 * LOCAL FIXTURES ARE NOT DESTROYED. The local library (fixtures/store.ts)
 * stays exactly as it was and remains the fallback when there is no bridge,
 * when scope is refused, or when the fetch fails. This module never writes
 * to it. Merging the two, and deciding who wins when both have edits, is a
 * real design question that needs the endpoint reachable first.
 *
 * STATUS (2026-08-02, OPBridge S175): the 403 above is resolved and the
 * wire now carries an operator-authored `name` (OPButtons's 2026-07-29 ask) —
 * consumed below with the `notes` / synthesised-label fallback chain the
 * field's own doc comment specifies. Not independently re-probed this
 * session (dev-PC bearer used for spot-checks had gone stale) — the fallback
 * chain degrades safely either way, so this is not a functional risk.
 *
 * RE-PROBED 2026-09-17 on the data plane with a live `opbuttons-full` bearer:
 * `GET /fixtures` → **200**. The 403 is confirmed gone on the wire, not
 * inferred from a peer's note. Two changes landed in the same pass:
 *   - profile resolution moved off the one-entry `BUILTIN_PROFILES` table and
 *     onto the live catalogue (`profileSync.ts`) - three of the operator's
 *     five real fixtures had been resolving nothing;
 *   - the translation now carries all eleven of OPBridge's fields plus a
 *     passthrough bag, and gained an inverse (`toBridgeFixture`), so the
 *     first write path cannot silently strip the mesh owner.
 */

import { createSignal, createEffect, createMemo, untrack, type Accessor } from 'solid-js';
import type { FixtureInstance } from '@opbuttons/schema';
import { bridges, getConnection } from '../opbridge/store.ts';
import { OpbridgeError, type FixtureInstance as WireFixture } from '../opbridge/httpClient.ts';
import { BUILTIN_PROFILES, localFixtures, fixtures, fixtureCapabilityNames, fixtureZones } from './store.ts';
import { bridgeProfile } from './profileSync.ts';

/**
 * Wire -> app translation. NOT a passthrough: the two `FixtureInstance`
 * types genuinely differ, the same way OPBridge's sequencer shape differed
 * at CP-067 (which needed port injection + camelCase kinds).
 *
 * **Label fallback chain: `name` -> `notes` -> synthesised.** OPBridge's
 * `name` is the operator-authored one ("Left PAR", "Front Wash") and wins
 * when present. `notes` is next — the pre-`name`-field convention (per
 * OPBridge S175) was operators parking a label there since it was the only
 * free-text field available, so a rig patched before 2026-08-02 still reads
 * correctly. Last resort is a synthesised label from the profile + DMX patch
 * - "SlimPAR 56 · 1.1" - true but not operator-chosen. Deliberately NEVER the
 * bare id: an unreadable label in a fixture list is how an operator patches
 * the wrong light mid-set.
 *
 * `mode` is optional on the wire and required here; falls back to the
 * profile's first mode, then to a literal so the object stays well-formed.
 * `positionHint.z` is optional on the wire and required in the app schema.
 */
export function fromBridgeFixture(w: WireFixture): FixtureInstance {
  // `[2026-09-17]` Profile resolution goes through the LIVE catalogue first.
  // This line used to read `BUILTIN_PROFILES[w.profileId]` - a one-entry
  // table - which is why the label chain's last resort was being reached at
  // all: measured against the operator's real rig, the COLORband PiX rendered
  // as "chauvet-dj/colorband-pix · 1.71", the bare-id outcome this function's
  // own doc comment forbids. The chain was never the bug.
  const profile = bridgeProfile(w.profileId)
    ?? BUILTIN_PROFILES[w.profileId];
  const mode = w.mode ?? profile?.modes[0]?.name ?? 'default';
  const label = profile?.model ?? profile?.name ?? w.profileId;
  const synthesised = `${label} · ${w.universe}.${w.dmxAddress}`;
  return {
    id: w.id,
    name: w.name?.trim() || w.notes?.trim() || synthesised,
    profileId: w.profileId,
    mode,
    universe: w.universe,
    dmxAddress: w.dmxAddress,
    ...(w.positionHint
      ? { positionHint: { x: w.positionHint.x, y: w.positionHint.y, z: w.positionHint.z ?? 0 } }
      : {}),
    ...(w.notes != null ? { notes: w.notes } : {}),
    // ─── Carried, not consumed (2026-09-17) ───────────────────────────────
    // OPBridge's instance has eleven fields; this function modelled eight and
    // dropped `outputNodeId` (the mesh owner, DEC-471), `groups` (the
    // operator's tags) and `environmentId` (the venue stamp, DEC-470). Free
    // while nothing writes; a silent re-homing of the operator's lights the
    // first time something does, because `PUT /fixtures/{id}` takes a whole
    // instance and never re-stamps the owner. Same class as the timeline
    // envelope (CP-139), in the write direction, fixed before it can bite.
    ...(w.outputNodeId != null ? { outputNodeId: w.outputNodeId } : {}),
    ...(Array.isArray(w.groups) && w.groups.length ? { groups: [...w.groups] } : {}),
    ...(w.environmentId != null ? { environmentId: w.environmentId } : {}),
    ...(w.transport != null ? { transport: w.transport } : {}),
    ...(() => {
      const extra = unknownWireFields(w);
      return Object.keys(extra).length ? { passthrough: extra } : {};
    })(),
    ...(profile ? { cachedProfile: profile } : {}),
  };
}

/** Every key this build models on the wire instance. Anything else OPBridge
 *  sends is additive from a newer daemon and is carried verbatim in
 *  `passthrough` rather than deleted on the next edit. Listed rather than
 *  derived so adding a modelled field above is a compile-visible pair of
 *  edits, the same discipline `timeline.ts`'s own key list uses. */
const KNOWN_WIRE_FIELDS = new Set([
  'id', 'profileId', 'mode', 'name', 'universe', 'dmxAddress',
  'positionHint', 'notes', 'outputNodeId', 'groups', 'environmentId', 'transport',
]);

function unknownWireFields(w: WireFixture): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(w)) {
    if (!KNOWN_WIRE_FIELDS.has(k) && v !== undefined) out[k] = v;
  }
  return out;
}

/**
 * App -> wire translation. The INVERSE of `fromBridgeFixture`, and the reason
 * the carried fields above exist: a `PUT /fixtures/{id}` body is a whole
 * instance, so every field we did not send is a field we deleted.
 *
 * `passthrough` is spread FIRST so a modelled field always wins over a stale
 * copy of itself - the bag should never contain one, but a future edit that
 * models a field without removing it from the bag must not resurrect the old
 * value.
 *
 * No caller yet, by design: `createFixture` / `updateFixture` have sat
 * uncalled on the client since they were written, and Phase 3 (the patch
 * surface) is the first thing that will reach for them. This exists so that
 * phase starts from a lossless converter instead of discovering the loss
 * against the operator's live rig.
 */
export function toBridgeFixture(f: FixtureInstance): WireFixture {
  return {
    ...(f.passthrough ?? {}),
    id: f.id,
    profileId: f.profileId,
    mode: f.mode,
    name: f.name,
    universe: f.universe,
    dmxAddress: f.dmxAddress,
    ...(f.positionHint ? { positionHint: f.positionHint } : {}),
    ...(f.notes != null ? { notes: f.notes } : {}),
    ...(f.outputNodeId != null ? { outputNodeId: f.outputNodeId } : {}),
    ...(f.groups?.length ? { groups: [...f.groups] } : {}),
    ...(f.environmentId != null ? { environmentId: f.environmentId } : {}),
    ...(f.transport != null ? { transport: f.transport } : {}),
  };
}

export type FixtureSyncStatus =
  /** Never fetched in this session. */
  | 'idle'
  /** Fetch in flight. */
  | 'loading'
  /** Fetched; the list is authoritative (possibly legitimately empty). */
  | 'ready'
  /** No connected primary OPBridge to ask. */
  | 'no-bridge'
  /** OPBridge refused: our bearer's bundle does not grant `/fixtures` (403).
   *  Distinct from `error` because the fix is a scope change on OPBridge, not
   *  anything the operator can do here. */
  | 'forbidden'
  /** The endpoint is absent on this daemon (404) - an older bridge. */
  | 'unavailable'
  /** Anything else: auth, network, malformed. `error` carries detail. */
  | 'error';

/**
 * The RAW wire response, held un-translated.
 *
 * `[2026-09-17]` This used to hold the already-translated `FixtureInstance[]`,
 * mapped once at fetch time. That was correct while `fromBridgeFixture` read
 * a compile-time constant table, and became a race the moment it started
 * reading the live profile catalogue: `/fixtures` and `/dmx/profiles` are two
 * requests fired by two effects off the same connect, so whichever loses
 * decides whether every label and capability list in the app is right for the
 * rest of the session - and a stored translation never re-runs to fix itself.
 *
 * Holding the raw response and DERIVING the translation removes the ordering
 * question instead of answering it: `bridgeFixtures()` re-maps whenever
 * either input changes, in whichever order they arrive. Costs one map over a
 * five-element array per read, which is nothing next to a label that is
 * silently wrong on the machine that only ever runs one of the two orders.
 */
const [rawSig, setRaw] = createSignal<WireFixture[]>([]);
const [statusSig, setStatus] = createSignal<FixtureSyncStatus>('idle');
const [errorSig, setError] = createSignal<string>('');
const [fetchedAtSig, setFetchedAt] = createSignal(0);

/** Fixtures as OPBridge reports them, translated against whatever the profile
 *  catalogue currently knows. Empty unless status is `ready` - callers must
 *  check the status rather than treating empty as "no rig".
 *
 *  `createMemo`, not a plain arrow: this is read by several surfaces per
 *  render and the mapping allocates. The memo also gives the dependency on
 *  `bridgeProfile`'s catalogue signal a single place to be tracked. */
export const bridgeFixtures: Accessor<FixtureInstance[]> = createMemo(() =>
  rawSig().map(fromBridgeFixture),
);
export const bridgeFixtureStatus = statusSig;
export const bridgeFixtureError = errorSig;
export const bridgeFixtureFetchedAt = fetchedAtSig;

/** True only when OPBridge answered and its list is the one to trust. */
export function bridgeFixturesAuthoritative(): boolean {
  return statusSig() === 'ready';
}

/** One-line state for a status row. Says what is wrong AND who fixes it -
 *  a bare "unavailable" sends the operator looking at their own rig for a
 *  problem that is a scope grant on another machine. */
export function fixtureSyncLabel(): string {
  switch (statusSig()) {
    case 'idle':        return 'Not read yet';
    case 'loading':     return 'Reading rig from OPBridge...';
    case 'ready':       return `${rawSig().length} fixture${rawSig().length === 1 ? '' : 's'} from OPBridge`;
    case 'no-bridge':   return 'No OPBridge connected - showing local fixtures';
    case 'forbidden':   return 'OPBridge refused: this bearer lacks /fixtures scope - showing local fixtures';
    case 'unavailable': return 'This OPBridge has no /fixtures endpoint - showing local fixtures';
    case 'error':       return `Could not read rig: ${errorSig()} - showing local fixtures`;
    default:            return '';
  }
}

/**
 * Read the rig from the primary OPBridge. Never throws: every failure lands
 * as a status, because a thrown fetch in a connect handler is a silent
 * failure and this module exists to make failure legible.
 */
export async function refreshBridgeFixtures(): Promise<void> {
  const primary = bridges().find((b) => b.is_primary) ?? bridges()[0];
  const conn = primary ? getConnection(primary.id) : undefined;
  if (!conn || conn.status() !== 'connected') {
    setStatus('no-bridge');
    setError('');
    return;
  }

  setStatus('loading');
  try {
    const res = await conn.httpClient().listFixtures();
    setRaw(Array.isArray(res.fixtures) ? res.fixtures : []);
    setFetchedAt(Date.now());
    setStatus('ready');
    setError('');
  } catch (err) {
    setRaw([]);
    if (err instanceof OpbridgeError) {
      if (err.httpStatus === 403) { setStatus('forbidden'); setError(err.message); return; }
      if (err.httpStatus === 404) { setStatus('unavailable'); setError(err.message); return; }
      setStatus('error');
      setError(`${err.kind}: ${err.message}`);
      return;
    }
    setStatus('error');
    setError((err as Error).message);
  }
}

/**
 * Re-read whenever a bridge reaches `connected`. Mirrors
 * `installStreamDeckPrewarm`: the cost is one request per connect, and it
 * means a reconnect mid-set re-syncs the rig instead of serving whatever was
 * true when the app booted.
 *
 * `untrack` around the fetch so this effect depends ONLY on connection
 * status - without it the signals written inside would re-trigger it and the
 * app would fetch in a loop. (The standing Solid trap: an effect that writes
 * a signal it also reads.)
 */
export function installFixtureSync(): void {
  createEffect(() => {
    const anyConnected = bridges().some((b) => getConnection(b.id)?.status() === 'connected');
    untrack(() => {
      if (anyConnected) void refreshBridgeFixtures();
      else { setStatus('no-bridge'); setRaw([]); }
    });
  });
}

// ─── DEV test hook (fixture sync state) ─────────────────────────────────
// Mirrors __opbuttonsClock / __opbuttonsBridges: a DEV-only window handle so bot
// smokes read the SAME live module instance the running app uses, rather than
// a fresh `page.evaluate(() => import(...))` re-import. That re-import pattern
// silently resolved to a SECOND, dead module instance once Vite's dev server
// had appended an HMR version query to the app's own import of this file (a
// long-lived dev server accumulates these over a session of live edits) - the
// smoke then polled a copy of `statusSig` nothing ever wrote to, reading
// "idle" forever while the real app had already reached "ready". Traced via
// `fixtures_read_from_opbridge` failing reliably pre-restart, passing reliably
// immediately after a dev-server restart, confirmed with an instance-tag probe
// showing two distinct tags in the same page. Stripped from production builds
// by the `import.meta.env.DEV` gate.
declare global {
  interface Window {
    __opbuttonsFixtures?: {
      status(): FixtureSyncStatus;
      label(): string;
      authoritative(): boolean;
      bridgeCount(): number;
      localCount(): number;
      shownCount(): number;
      /** The TRANSLATED rows a surface would actually render, with the
       *  capability count each one resolved to. Counts alone cannot catch the
       *  2026-09-17 defect: the list was the right LENGTH the whole time while
       *  three of five rows carried no capabilities and one showed its raw
       *  profileId as its name. A smoke has to see the rows. */
      rows(): Array<{ id: string; name: string; profileId: string; mode: string; capCount: number; zoneCount: number; groups: string[] }>;
    };
  }
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.__opbuttonsFixtures = {
    status: () => statusSig(),
    label: fixtureSyncLabel,
    authoritative: bridgeFixturesAuthoritative,
    bridgeCount: () => rawSig().length,
    localCount: () => localFixtures().length,
    shownCount: () => fixtures().length,
    rows: () => fixtures().map((f) => ({
      id: f.id,
      name: f.name,
      profileId: f.profileId,
      mode: f.mode,
      capCount: fixtureCapabilityNames(f).length,
      zoneCount: fixtureZones(f).length,
      groups: f.groups ?? [],
    })),
  };
}
